import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { apiRequest, HC_API_HOST } from "../../services/fetchCaseApi";

// Extract a single party name from an "Name Advocate- ..." string.
const extractPartyName = (advocateString) => {
  if (!advocateString || typeof advocateString !== "string") return "N/A";
  const parts = advocateString.split("Advocate-");
  return parts.length > 0 ? parts[0].trim().replace(/^\d+\) /, "") : "N/A";
};

// Extract every party name from a petitioner/respondent advocate array so all
// parties (not just the first) are preserved on export. Returns objects in the
// { name, relation, relationName } shape used across the app for multi-party cases.
export const extractAllPartyNames = (advocateArray) => {
  if (!Array.isArray(advocateArray)) return [];
  return advocateArray
    .map((str) => extractPartyName(str))
    .filter((name) => name && name !== "N/A")
    .map((name) => ({ name, relation: "", relationName: "" }));
};

// Verify a downloaded blob is actually a PDF (not the eCourts
// "Orders is not uploaded" HTML/JSON error) before we persist it.
const isPdfBlob = async (blob) => {
  if (!blob || blob.size === 0) return false;
  try {
    const header = await blob.slice(0, 5).text();
    return header.startsWith("%PDF");
  } catch {
    return false;
  }
};

// Fetch a single order's PDF blob using the same paths as the viewer.
const fetchOrderPdfBlob = async (order, cookies) => {
  if (order?.pdfProxyUrl) {
    const res = await fetch(`${HC_API_HOST}${order.pdfProxyUrl}`);
    if (!res.ok) throw new Error(`PDF fetch failed: ${res.status}`);
    return await res.blob();
  }
  if (order?.orderLink) {
    const hcservices_sessid = cookies?.HCSERVICES_SESSID || "";
    const jsession_value = cookies?.JSESSION || cookies?.JSESSIONID || "";
    return await apiRequest("case/orders/pdf", {
      orderLink: order.orderLink,
      hcservices_sessid,
      jsession_value,
    });
  }
  return null;
};

// Download each order PDF (while the session is fresh) and upload it to
// Firebase Storage, returning the orders enriched with a permanent
// `pdfStorageUrl`. Best-effort: a failed order keeps its original links.
export const persistOrderPdfs = async (orders, cookies, ownerId, caseNo) => {
  if (!Array.isArray(orders) || orders.length === 0) return orders || [];
  const safeCaseNo = String(caseNo || "case").replace(/[^a-zA-Z0-9._-]/g, "_");
  const enriched = [];
  for (let i = 0; i < orders.length; i++) {
    const order = orders[i];
    let pdfStorageUrl = "";
    try {
      const blob = await fetchOrderPdfBlob(order, cookies);
      if (blob && (await isPdfBlob(blob))) {
        const path = `highcourt-orders/${ownerId}/${safeCaseNo}/order_${i}_${Date.now()}.pdf`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, blob, { contentType: "application/pdf" });
        pdfStorageUrl = await getDownloadURL(storageRef);
      } else {
        console.warn(`Order ${i}: PDF unavailable at export (not a PDF), keeping live links`);
      }
    } catch (err) {
      console.warn(`Order ${i}: could not persist PDF:`, err.message);
    }
    enriched.push({ ...order, pdfStorageUrl });
  }
  return enriched;
};

// Deterministic file name for a single Court Orders record, used both to store
// the file and to detect "already exported" (dedupe by name).
export const courtOrderFileName = (order) => {
  const dateSlug = String(order?.order_dt || order?.order_year || "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${dateSlug || "order"}_order_${order?.order_no ?? "x"}.pdf`;
};

// Export ONE Court Orders record into an already-exported case:
//   1. fetch the order PDF (session-gated) and verify it's a real PDF,
//   2. upload it to Firebase Storage,
//   3. register it under the case's "Orders" folder (Files tab),
//   4. append it to the case doc's `orders` array (Orders tab) + files[].
// Deduped by file name — re-exporting the same order is a no-op.
// Returns { status: 'exported' | 'skipped' | 'no-pdf', fileName }.
export const exportSingleCourtOrder = async ({
  order,
  caseDoc,
  caseCollection,
  ownerId,
  cookies,
  courtCode,
  stateCode,
}) => {
  const caseId = caseDoc.id;
  const caseNo = caseDoc.caseNo || "";
  const fileName = courtOrderFileName(order);

  // Already exported? (same case + same file name). Query by caseId only (an
  // existing single-field index) and match the name client-side, so no new
  // Firestore composite index is needed.
  const dupSnap = await getDocs(
    query(collection(db, "files"), where("caseId", "==", caseId))
  );
  if (dupSnap.docs.some((d) => d.data().fileName === fileName)) {
    return { status: "skipped", fileName };
  }

  // display_pdf.php caseno is "<type>/<reg_no>/<reg_year>" (e.g. "CRLA/4844/2026").
  const caseno =
    order.type_name && order.reg_no && order.reg_year
      ? `${order.type_name}/${order.reg_no}/${order.reg_year}`
      : caseNo;

  const blob = await apiRequest("court-orders/pdf", {
    orderurlpath: order.orderurlpath,
    caseno,
    cCode: courtCode,
    cino: order.cino,
    state_code: stateCode,
    flag: "nojudgement",
    hcservices_sessid: cookies?.HCSERVICES_SESSID || "",
    jsession_value: cookies?.JSESSION || cookies?.JSESSIONID || "",
  });
  if (!blob || !(await isPdfBlob(blob))) return { status: "no-pdf", fileName };

  const safeCaseNo = String(caseNo || "case").replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `highcourt-orders/${ownerId}/${safeCaseNo}/co_${order.order_no ?? 0}_${Date.now()}.pdf`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, { contentType: "application/pdf" });
  const pdfStorageUrl = await getDownloadURL(storageRef);

  const folderId = await ensureOrdersFolder(caseId, caseNo, ownerId);
  await addDoc(collection(db, "files"), {
    caseNo,
    caseId,
    fileName,
    file_url: pdfStorageUrl,
    lawyer_uid: ownerId,
    uploadedAt: new Date().toISOString(),
    folderId,
    source: "highcourt-court-order",
  });

  // Mirror into the case's Orders tab (case doc orders[]) using the same shape
  // the details screen renders, plus a permanent pdfStorageUrl for viewing.
  const orderEntry = {
    orderNumber: String(order.order_no ?? ""),
    orderOn: (order.docu_name || "Order").trim(),
    orderDate: order.order_dt || order.order_year || "",
    judge: "",
    orderLink: "",
    pdfProxyUrl: "",
    pdfStorageUrl,
    cino: order.cino || "",
    source: "court-order",
  };
  await updateDoc(doc(db, caseCollection, caseId), {
    orders: arrayUnion(orderEntry),
    files: arrayUnion(pdfStorageUrl),
  });

  return { status: "exported", fileName };
};

// Get-or-create the per-case "Orders" folder (keyed on caseId + name + owner)
// so re-exports don't pile up duplicate folders. Mirrors ngtFileSync.js.
const ensureOrdersFolder = async (caseId, caseNo, ownerId) => {
  const snap = await getDocs(
    query(
      collection(db, "folders"),
      where("caseId", "==", caseId),
      where("name", "==", "Orders"),
      where("lawyer_uid", "==", ownerId)
    )
  );
  if (!snap.empty) return snap.docs[0].id;
  const created = await addDoc(collection(db, "folders"), {
    name: "Orders",
    caseId: caseId,
    caseNo: caseNo || "",
    lawyer_uid: ownerId,
    createdAt: new Date().toISOString(),
  });
  return created.id;
};

// Mirror each order into the case's Files tab under an "Orders" folder, so the
// orders are available alongside manually uploaded documents. Each file is named
// `<OrderDate>_order.pdf`. Like the NCLT/NGT flows, this registers a file for
// EVERY order using the best viewable URL (the persisted Firebase PDF when the
// upload succeeded, otherwise the live order link) rather than skipping orders
// whose blob upload failed. Best-effort: failures here never block the export.
export const saveOrderPdfsToFiles = async (orders, caseId, caseNo, caseCollection, ownerId) => {
  const list = (Array.isArray(orders) ? orders : [])
    .map((o) => ({
      order: o,
      url:
        o?.pdfStorageUrl ||
        (o?.pdfProxyUrl ? `${HC_API_HOST}${o.pdfProxyUrl}` : "") ||
        o?.orderLink ||
        "",
    }))
    .filter((x) => x.url);
  if (list.length === 0) return;

  let folderId;
  try {
    folderId = await ensureOrdersFolder(caseId, caseNo, ownerId);
  } catch (err) {
    console.warn("Could not create Orders folder:", err.message);
    return;
  }

  // Disambiguate filenames when two orders share the same date.
  const seen = new Map();
  const newUrls = [];
  for (let i = 0; i < list.length; i++) {
    const { order, url } = list[i];
    const dateSlug = String(order.orderDate || "")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const base = `${dateSlug || `order-${i + 1}`}_order.pdf`;
    const count = (seen.get(base) || 0) + 1;
    seen.set(base, count);
    const fileName = count === 1 ? base : base.replace(/\.pdf$/, `_${count}.pdf`);
    try {
      await addDoc(collection(db, "files"), {
        caseNo: caseNo || "",
        caseId: caseId,
        fileName: fileName,
        file_url: url,
        lawyer_uid: ownerId,
        uploadedAt: new Date().toISOString(),
        folderId: folderId,
        source: "highcourt-order",
      });
      newUrls.push(url);
    } catch (err) {
      console.warn(`Could not save order ${i} to files:`, err.message);
    }
  }

  // Keep the case document's files[] array in sync (backward compatibility).
  if (newUrls.length > 0 && caseCollection) {
    try {
      const caseRef = doc(db, caseCollection, caseId);
      const snap = await getDoc(caseRef);
      if (snap.exists()) {
        await updateDoc(caseRef, { files: arrayUnion(...newUrls) });
      }
    } catch (err) {
      console.warn("Could not update case files[]:", err.message);
    }
  }
};
